import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchFiles } from '../services/apiService';
import { useFileBrowser } from '../hooks/useFileBrowser';
import { useDarkMode } from '../hooks/useDarkMode';
import Header from './Header';
import Breadcrumbs from './Breadcrumbs';
import LoadingSpinner from './LoadingSpinner';
import EmptyState from './EmptyState';
import FileItem from './FileItem';
import DynamicIcon from './DynamicIcon';
import DownloadButton from './DownloadButton';
import { formatBytes } from '../utils/formatBytes';

const DOWNLOADABLE_FILE = /\.(?:zip|rar|7z|tar|gz|apk|img|bin|iso|md5|sha1|sha256|exe|msi|pdf)$/i;

const formatFileDate = (date) => {
    if (!date) return null;
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return null;

    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(parsedDate);
};

const FileBrowser = () => {
    const [darkMode, toggleDarkMode] = useDarkMode();
    // Set a default state, which will be updated on the client-side
    const [viewMode, setViewMode] = useState('list');
    const params = useParams();
    const currentPath = params['*'] || '';
    const pathParts = currentPath.split('/').filter(Boolean);
    const requestedFileName = DOWNLOADABLE_FILE.test(pathParts[pathParts.length - 1] || '') ? pathParts[pathParts.length - 1] : null;
    const folderPath = requestedFileName ? pathParts.slice(0, -1).join('/') : currentPath;

    // Set the initial view mode based on screen size, but don't force it on resize
    useEffect(() => {
        const isMobile = window.innerWidth < 768;
        setViewMode(isMobile ? 'list' : 'grid');
    }, []); // Empty dependency array ensures this effect runs only ONCE on mount

    const { data, isLoading, error } = useQuery({
        queryKey: ['files', folderPath],
        queryFn: () => fetchFiles(folderPath),
        keepPreviousData: true,
    });

    const hasFiles = useMemo(() => data?.files?.some(file => !file.isFolder) || false, [data?.files]);
    const requestedFile = useMemo(() => (
        requestedFileName ? data?.files?.find(file => !file.isFolder && file.name === requestedFileName) : null
    ), [data?.files, requestedFileName]);

    const latestBuildId = useMemo(() => {
        const getBuildDate = file => file.fileDateTime;
        const builds = data?.files?.filter(file => (
            !file.isFolder && /OTA.*\d{8}[-_]\d{4}.*\.zip$/i.test(file.name) && getBuildDate(file)
        )) || [];
        return builds.reduce((latest, file) => (
            !latest || new Date(getBuildDate(file)) > new Date(getBuildDate(latest)) ? file : latest
        ), null)?.id;
    }, [data?.files]);

    const breadcrumbs = useMemo(() => {
        if (!data?.currentFolder) return [];
        const newBreadcrumbs = [{ name: 'Root', path: '', key: 'root' }];
        if (folderPath) {
            folderPath.split('/').reduce((pathAccumulator, segment) => {
                const newPath = `${pathAccumulator ? `${pathAccumulator}/` : ''}${segment}`;
                newBreadcrumbs.push({ name: decodeURIComponent(segment), path: newPath, key: newPath });
                return newPath;
            }, '');
        }
        if (newBreadcrumbs.length > 1 && data.currentFolder) {
            newBreadcrumbs[newBreadcrumbs.length - 1].name = data.currentFolder.name;
        }
        if (requestedFileName) {
            newBreadcrumbs.push({ name: requestedFileName, path: currentPath, key: currentPath });
        }
        return newBreadcrumbs;
    }, [currentPath, folderPath, requestedFileName, data?.currentFolder]);

    const { handleFolderClick, handleBreadcrumbClick, handleDownload, goBack } = useFileBrowser(folderPath, breadcrumbs);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                when: "beforeChildren",
                staggerChildren: 0.05,
            },
        },
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 100 } },
    };

    return (
        <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-[#100f15]' : 'bg-slate-50'}`}>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Header
                    darkMode={darkMode}
                    viewMode={viewMode}
                    toggleViewMode={() => setViewMode(v => v === 'grid' ? 'list' : 'grid')}
                    toggleDarkMode={toggleDarkMode}
                />

                <motion.div layout className={`rounded-2xl overflow-hidden ${darkMode ? 'bg-[#1a1823]/80 border border-slate-700/50' : 'bg-white/80 border border-slate-200'} shadow-xl shadow-black/10 backdrop-blur-sm`}>
                    <div className="p-5 bg-gradient-to-r from-blue-600 to-blue-500">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-center mb-4 sm:mb-0">
                                <motion.button
                                    onClick={goBack}
                                    disabled={breadcrumbs.length <= 1}
                                    aria-label="Go back"
                                    whileHover={{ scale: breadcrumbs.length > 1 ? 1.1 : 1, backgroundColor: breadcrumbs.length > 1 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.05)' }}
                                    whileTap={{ scale: breadcrumbs.length > 1 ? 1.0 : 1 }}
                                    className={`mr-3 p-2.5 rounded-xl transition-colors duration-200 ${breadcrumbs.length > 1 ? 'bg-white/10 text-white' : 'bg-white/5 text-white/50 cursor-not-allowed'}`}
                                >
                                    <DynamicIcon name="arrow" darkMode={darkMode} className="h-4 w-4" />
                                </motion.button>
                                <h2 className="text-xl font-semibold text-white truncate max-w-xs sm:max-w-md">
                                    {data?.currentFolder?.name || 'Loading...'}
                                </h2>
                            </div>
                            <Breadcrumbs items={breadcrumbs} darkMode={darkMode} onClick={handleBreadcrumbClick} />
                        </div>
                    </div>

                    <div className="p-4 sm:p-6 min-h-[600px]">
                        {isLoading && !data ? ( // Show spinner only on initial load
                            <LoadingSpinner darkMode={darkMode} />
                        ) : error ? (
                            <div className={`p-6 text-center rounded-xl m-4 ${darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'}`}>
                                <div className="font-medium mb-1">Unable to load content</div>
                                <div className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>{error.message}</div>
                            </div>
                        ) : (
                            <AnimatePresence>
                            {requestedFileName ? (
                                requestedFile ? (
                                    <motion.div
                                        initial={{ opacity: 0, y: 12 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`max-w-2xl mx-auto p-6 sm:p-8 rounded-2xl border shadow-lg ${darkMode ? 'bg-[#1a1823] border-slate-700/50' : 'bg-white border-slate-200'}`}
                                    >
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                                            <div className={`p-4 rounded-xl flex-shrink-0 self-start ${darkMode ? 'bg-cyan-500/10' : 'bg-cyan-100'}`}>
                                                <DynamicIcon name="file" darkMode={darkMode} className="h-8 w-8" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <h3 className={`text-lg sm:text-xl font-semibold break-all leading-snug ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                                        {requestedFile.name}
                                                    </h3>
                                                    {requestedFile.id === latestBuildId && (
                                                        <span className="flex-shrink-0 px-2 py-1 rounded bg-[#20c747] text-[#07130a] text-xs font-semibold">
                                                            LATEST
                                                        </span>
                                                    )}
                                                </div>
                                                <div className={`mt-3 space-y-1 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                    {/OTA.*\d{8}[-_]\d{4}.*\.zip$/i.test(requestedFile.name) && formatFileDate(requestedFile.fileDateTime) && (
                                                        <p>Release date: {formatFileDate(requestedFile.fileDateTime)}</p>
                                                    )}
                                                    <p>Size: {formatBytes(requestedFile.size)}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <DownloadButton
                                            darkMode={darkMode}
                                            onClick={(e) => handleDownload(requestedFile, e)}
                                            label="Download"
                                            className="mt-6 w-full sm:w-auto px-5 py-3 rounded-xl bg-blue-600 text-white inline-flex items-center justify-center gap-2 font-semibold"
                                        />
                                    </motion.div>
                                ) : (
                                    <div className={`p-6 text-center rounded-xl ${darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'}`}>
                                        File not found.
                                    </div>
                                )
                            ) : data.files?.length > 0 ? (
                                <motion.div
                                    key={viewMode}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="relative"
                                >
                                {isLoading && (
                                    <div className="absolute inset-0 bg-black/10 dark:bg-black/20 backdrop-blur-sm z-20 flex items-center justify-center">
                                        {/* This wrapper prevents the spinner from stretching during layout animations */}
                                        <div className="flex-shrink-0">
                                            <LoadingSpinner darkMode={darkMode} />
                                        </div>
                                    </div>
                                )}
                                {viewMode === 'grid' ? (
                                    <motion.ul
                                        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5"
                                        variants={containerVariants}
                                        initial="hidden"
                                        animate="visible"
                                    >
                                        {data.files.map((file) => (
                                            <FileItem key={file.id} file={file} darkMode={darkMode} onClick={handleFolderClick} onDownload={(e) => handleDownload(file, e)} isGridView={true} isLatest={file.id === latestBuildId} variants={itemVariants} />
                                        ))}
                                    </motion.ul>
                                ) : (
                                    <motion.div
                                        className={`rounded-xl border ${darkMode ? 'border-slate-700/50' : 'border-slate-200'} shadow-md shadow-black/10 overflow-hidden`}
                                        variants={containerVariants}
                                        initial="hidden"
                                        animate="visible"
                                    >
                                        <div className={`flex justify-between items-center w-full ${darkMode ? 'bg-[#1a1823]' : 'bg-slate-50/80'} border-b ${darkMode ? 'border-slate-700/50' : 'border-slate-200'}`}>
                                            <div className={`py-3 pl-6 pr-3 text-left text-xs font-semibold uppercase tracking-wider flex-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Name</div>
                                            <div className="flex items-center flex-shrink-0">
                                                <div className={`py-3 px-3 text-center text-xs font-semibold uppercase tracking-wider w-28 hidden sm:block ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Type</div>
                                                <div className={`py-3 px-3 text-center text-xs font-semibold uppercase tracking-wider w-28 hidden sm:block ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Size</div>
                                                {hasFiles && (
                                                    <div className={`py-3 pl-2 pr-3 sm:pl-3 sm:pr-6 text-right text-xs font-semibold uppercase tracking-wider w-16 sm:w-24 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Actions</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="min-h-[600px]">
                                            {data.files.map((file) => (
                                                <FileItem
                                                    key={file.id}
                                                    file={file}
                                                    darkMode={darkMode}
                                                    onClick={handleFolderClick}
                                                    onDownload={(e) => handleDownload(file, e)}
                                                    isGridView={false}
                                                    showActionsColumn={hasFiles}
                                                    isLatest={file.id === latestBuildId}
                                                    variants={itemVariants}
                                                />
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                                </motion.div>
                            ) : (
                                <EmptyState darkMode={darkMode} />
                            )}
                            </AnimatePresence>
                        )}
                    </div>
                </motion.div>

                <footer className={`text-center mt-10 pt-6 border-t ${darkMode ? 'text-slate-400 border-slate-800' : 'text-slate-500 border-slate-200'}`}>
                    <p className={`font-medium ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>OTAForge · Secure & Verified Downloads</p>
                    <p className="mt-1 text-sm">&copy; {new Date().getFullYear()} AndroidOne Experience. All rights reserved.</p>
                </footer>
            </div>
        </div>
    );
};

export default FileBrowser;
